import React, { useState } from 'react';

import { useStudentsQuery } from '@school_ify/schoolify-repository/src/teacher/types';

import CheckBox from 'clean/components/common/CheckBox/CheckBox';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import StyledTooltip from 'components/StyledTooltip/StyledTooltip';

import styles from './MarksTable.module.scss';
import MarksTableHeader from '../MarksTableHeader/MarksTableHeader';
import EditSingleUserGradeModal from '../EditSingleUserGradeModal/EditSingleUserGradeModal';
import EditMultipleUserGradesModal from '../EditMultipleUserGradesModal/EditMultipleUserGradesModal';
import SemesterGradeModal from '../SemesterGradeModal/SemesterGradeModal';
import helper from './helper';

export enum ViewMode {
  'date',
  'compact',
}

const MarksTable: React.FC = () => {
  const { data: students } = useStudentsQuery();
  const gradeableEvents =
    students?.students.map((item, index) => index + 1).filter((item, index) => index < 300) || [];

  const [checked, setChecked] = useState([]);
  const allChecked = checked.length === students?.students.length;

  const onCheckAll = () => {
    setChecked(allChecked ? [] : students?.students.map(s => s.id));
  };

  const onCheck = id => {
    setChecked(checked.includes(id) ? checked.filter(c => c !== id) : [...checked, id]);
  };

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.date);

  const [showEditSingleUserGradeModal, setShowEditSingleUserGradeModal] = useState(false);
  const [showEditMultipleUserGradesModal, setShowEditMultipleUserGradesModal] = useState(false);
  const [showSemesterModal, setShowSemesterModal] = useState(false);

  const compactViewCellCount = (() => {
    if (viewMode === ViewMode.compact) {
      return Math.max(gradeableEvents.length, 1);
    }
    return 4;
  })();

  return (
    <div className={styles.marksTableContainer}>
      {showSemesterModal && <SemesterGradeModal onClose={() => setShowSemesterModal(false)} />}
      {showEditSingleUserGradeModal && (
        <EditSingleUserGradeModal onClose={() => setShowEditSingleUserGradeModal(false)} />
      )}
      {showEditMultipleUserGradesModal && (
        <EditMultipleUserGradesModal onClose={() => setShowEditMultipleUserGradesModal(false)} />
      )}

      <MarksTableHeader setViewMode={setViewMode} viewMode={viewMode} />
      <div className={styles.marksTableWrapper}>
        <table>
          <thead>
            <tr>
              <th className={styles.cellStudent}>
                <div className={styles.cellStudentContainer}>
                  <div className={styles.checkboxContainer} onMouseDown={onCheckAll}>
                    <CheckBox color="orange" onChange={() => null} checked={allChecked} />
                  </div>
                  <div className={styles.title}>Student</div>
                </div>
              </th>
              {viewMode === ViewMode.compact && (
                <th colSpan={compactViewCellCount} className={styles.gradesColumnHeader}>
                  Grades
                </th>
              )}
              {viewMode === ViewMode.date &&
                gradeableEvents.map(ev => {
                  return (
                    <th
                      onMouseEnter={e =>
                        helper.onHeaderEnter(e, styles.marksTableWrapper, styles.forceHover)
                      }
                      onMouseLeave={() => helper.onHeaderLeave(styles.forceHover)}
                      key={ev}
                      className={styles.cellEventHeader}
                      onClick={() => setShowEditMultipleUserGradesModal(true)}
                    >
                      <StyledTooltip content={<p>{'Edit column'}</p>}>
                        <p></p>
                      </StyledTooltip>
                      <div>
                        {ev}.{ev}
                      </div>
                    </th>
                  );
                })}

              <th
                className={styles.cellSemesterHeader}
                onClick={() => setShowSemesterModal(true)}
                onMouseEnter={e =>
                  helper.onHeaderEnter(e, styles.marksTableWrapper, styles.forceHover)
                }
                onMouseLeave={() => helper.onHeaderLeave(styles.forceHover)}
              >
                <StyledTooltip content={<p>{'Edit semester'}</p>}>
                  <p></p>
                </StyledTooltip>
                <div>{'Semester'}</div>
              </th>
            </tr>
          </thead>

          <tbody>
            {students?.students.map(student => (
              <tr key={student.id}>
                <td className={styles.cellStudent}>
                  <div className={styles.cellStudentContainer}>
                    <div
                      className={styles.checkboxContainer}
                      onMouseDown={() => onCheck(student.id)}
                    >
                      <CheckBox
                        color="orange"
                        onChange={() => null}
                        checked={checked.includes(student.id)}
                      />
                    </div>
                    <div className={styles.title}>{student?.user?.full_name}</div>
                  </div>
                </td>
                {viewMode === ViewMode.compact &&
                  (() => {
                    const gradeCells = gradeableEvents
                      .filter(() => Math.random() * 100 > 50)
                      .map(e => (
                        <td key={e} className={styles.cellEvent}>
                          {e}
                        </td>
                      ));

                    const emptyCellCount = compactViewCellCount - gradeCells.length;

                    return [
                      gradeCells,
                      Array(emptyCellCount)
                        .fill(null)
                        .map((item, index) => <td key={index} className={styles.cellEvent}></td>),
                    ];
                  })()}

                {viewMode === ViewMode.date &&
                  gradeableEvents.map(e => {
                    return (
                      <td
                        key={e}
                        className={styles.cellEvent}
                        onClick={() => setShowEditSingleUserGradeModal(true)}
                      >
                        {Math.random() > 0.5 ? (
                          <div className={styles.gradeAdd}>
                            <PlusIcon />
                          </div>
                        ) : (
                          <div className={styles.semester}>{e}</div>
                        )}
                      </td>
                    );
                  })}
                <td className={styles.cellSemester} onClick={() => setShowSemesterModal(true)}>
                  {Math.random() > 0.5 ? (
                    <div className={styles.semesterAdd}>
                      <PlusIcon />
                    </div>
                  ) : (
                    <div className={styles.semester}>+2</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarksTable;
